/**
 * Quote list ("Add to Quote" / "View Quote List" / "Submit Quote Request")
 * for The Hospital Equipment Company.
 *
 * - Quote items live ONLY in localStorage. The only cart call is a read of /cart.js
 *   for "Request a quote for this cart"; the cart itself is never changed.
 * - Submissions go straight to a HubSpot form (portal set in window.QuoteRequestConfig).
 * - $0 ("contact for pricing") variants hide Add to Cart and the price, and any
 *   attempt to add a $0 variant to the cart is blocked as a safety net.
 */
(function () {
  'use strict';

  if (window.__quoteRequestLoaded) return;
  window.__quoteRequestLoaded = true;

  var CFG = window.QuoteRequestConfig || {};
  var STORAGE_KEY = 'thec_quote_list_v1';
  var memoryStore = [];

  /* ------------------------------------------------------------------ */
  /* Storage                                                              */
  /* ------------------------------------------------------------------ */

  function loadItems() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return memoryStore.slice();
    }
  }

  function saveItems(items) {
    memoryStore = items.slice();
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) { /* private mode: keep in memory */ }
    renderAll();
  }

  function totalQty(items) {
    return items.reduce(function (sum, item) { return sum + (parseInt(item.qty, 10) || 0); }, 0);
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                              */
  /* ------------------------------------------------------------------ */

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function parseJSON(str) {
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  function isDefaultTitle(title) {
    return !title || title === 'Default Title';
  }

  function findVariant(product, id) {
    id = String(id);
    var list = (product && product.variants) || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === id) return list[i];
    }
    return null;
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* ------------------------------------------------------------------ */
  /* Adding items                                                         */
  /* ------------------------------------------------------------------ */

  // Extra configuration fields (config-guide section): elements with data-quote-prop.
  function collectProps(scope, button, product) {
    if (button.getAttribute('data-quote-context') !== 'page') return [];
    var fields = scope ? scope.querySelectorAll('[data-quote-prop]') : [];
    if (!fields.length && product) {
      var guide = document.querySelector('[data-config-guide][data-product-id="' + product.id + '"]');
      if (guide) fields = guide.querySelectorAll('[data-quote-prop]');
    }
    var map = {}, order = [];
    Array.prototype.forEach.call(fields, function (el) {
      var name = el.getAttribute('data-quote-prop');
      var val = '';
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (!el.checked) return;
        val = el.value;
      } else {
        val = (el.value || '').trim();
      }
      if (!name || !val) return;
      if (!map.hasOwnProperty(name)) { map[name] = []; order.push(name); }
      map[name].push(val);
    });
    return order.map(function (n) { return { name: n, value: map[n].join('; ') }; });
  }

  // Compatibility rules from the configuration guide (select fields with data-rules), e.g.
  // {prop: "Upholstery top", when: "heated", requires: "heated-top ready", message: "..."}.
  // Stored on the quote line so the drawer can re-check them when the base model changes.
  function collectRules(scope, button, product) {
    if (button.getAttribute('data-quote-context') !== 'page') return [];
    var guide = product ? document.querySelector('[data-config-guide][data-product-id="' + product.id + '"]') : null;
    var root = guide || scope;
    if (!root) return [];
    var out = [];
    Array.prototype.forEach.call(root.querySelectorAll('select[data-rules][data-quote-prop]'), function (sel) {
      var rules = [];
      try { rules = JSON.parse(sel.getAttribute('data-rules')) || []; } catch (e) {}
      rules.forEach(function (r) {
        if (r && r.when && r.requires) out.push({ prop: sel.getAttribute('data-quote-prop'), when: String(r.when), requires: String(r.requires), message: String(r.message || '') });
      });
    });
    return out;
  }

  // Remove props that are incompatible with the line's current variant. Returns the
  // messages for anything removed (empty array when the line is valid).
  function enforceRules(item) {
    var removed = [];
    if (!item || !item.rules || !item.rules.length || !item.props || !item.props.length) return removed;
    var variantText = String(item.variantTitle || '').toLowerCase();
    item.props = item.props.filter(function (p) {
      var bad = null;
      item.rules.forEach(function (r) {
        if (bad) return;
        if (r.prop !== p.name) return;
        var ok = variantText.indexOf(r.requires.toLowerCase()) > -1;
        if (!ok && String(p.value).toLowerCase().indexOf(r.when.toLowerCase()) > -1) bad = r;
      });
      if (bad) { removed.push('Removed "' + p.value + '": ' + (bad.message || ('not available on ' + item.variantTitle)) + ' Choose a compatible option on the product page.'); return false; }
      return true;
    });
    if (removed.length) item.propsKey = propsKey(item.props);
    return removed;
  }

  // Required companion hardware from the configuration guide (guide.requirements), e.g.
  // {if: [{field: "Upholstery top", contains: "32 in"}, ...], need: {field: "Accessories", value: "..."},
  //  message: "...", link: "/products/..."}. Stored on the quote line and re-checked in the drawer.
  function collectRequirements(scope, button, product) {
    if (button.getAttribute('data-quote-context') !== 'page') return [];
    var guide = product ? document.querySelector('[data-config-guide][data-product-id="' + product.id + '"]') : null;
    var el = guide && guide.querySelector('[data-config-guide-requirements]');
    var list = el ? (parseJSON(el.textContent) || []) : [];
    return list.filter(function (r) {
      return r && r.need && r.need.field && r.need.value && Array.isArray(r['if']);
    }).map(function (r) {
      return {
        'if': r['if'].map(function (c) { return { field: String(c.field || ''), contains: String(c.contains || '') }; }),
        need: { field: String(r.need.field), value: String(r.need.value) },
        message: String(r.message || ''),
        link: String(r.link || ''),
        action_label: String(r.action_label || '')
      };
    });
  }

  function lineValues(item, field) {
    if (String(field).toLowerCase() === 'variant') return [String(item.variantTitle || '')];
    var out = [];
    (item.props || []).forEach(function (p) {
      if (p.name !== field) return;
      String(p.value).split('; ').forEach(function (v) { if (v) out.push(v); });
    });
    return out;
  }

  // Requirements of a line that are triggered but not satisfied by its props.
  function unmetRequirements(item) {
    if (!item || !item.requirements || !item.requirements.length) return [];
    return item.requirements.filter(function (req) {
      var needVal = String(req.need.value).toLowerCase();
      var triggered = req['if'].every(function (c) {
        var contains = String(c.contains).toLowerCase();
        return lineValues(item, c.field).some(function (v) {
          v = v.toLowerCase();
          if (c.field === req.need.field && v === needVal) return false;
          return v.indexOf(contains) > -1;
        });
      });
      if (!triggered) return false;
      return lineValues(item, req.need.field).map(function (v) { return v.toLowerCase(); }).indexOf(needVal) === -1;
    });
  }

  function partNumber(value) {
    var m = String(value).match(/\(([^()]+)\)\s*$/);
    return m ? m[1] : value;
  }

  // Lines saved before Oct 19 2026 have no action_label: borrow it from the product's
  // configuration guide when that product page is open.
  function guideActionLabel(item, req) {
    var guide = document.querySelector('[data-config-guide][data-product-id="' + item.productId + '"] [data-config-guide-requirements]');
    var list = guide ? (parseJSON(guide.textContent) || []) : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].need && list[i].need.value === req.need.value && list[i].action_label) return String(list[i].action_label);
    }
    return '';
  }

  // Drawer button text, e.g. "Add receiver extensions (9A582001)": the requirement's action_label
  // (without "to this quote line") plus the part number; otherwise "Add " + the needed value.
  function requirementLabel(item, req) {
    var label = String(req.action_label || guideActionLabel(item, req) || '')
      .replace(/\s+to\s+(this|the|your)\s+quote(\s+line|\s+list)?\.?\s*$/i, '').trim();
    if (!label) return 'Add ' + req.need.value;
    var pn = partNumber(req.need.value);
    if (pn && pn !== req.need.value && label.indexOf(pn) === -1) label += ' (' + pn + ')';
    return label;
  }

  // Short "Added: ..." confirmation shown on a line for ~4 s after a requirement is added.
  var CONFIRM_MS = 4000;
  var confirmations = {};
  function lineKey(item) {
    return String(item.variantId) + '|' + (item.propsKey || '');
  }

  function announce(text) {
    if (!drawer && !cacheDrawer()) return;
    var live = drawer.querySelector('[data-quote-live]');
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 60);
    clearTimeout(live.__clearTimer);
    live.__clearTimer = setTimeout(function () { live.textContent = ''; }, CONFIRM_MS + 500);
  }

  // Add a requirement's needed value to a line's props (checkbox-style fields join with "; ").
  function addRequirement(index, reqIndex) {
    var items = loadItems();
    var item = items[index];
    if (!item || !item.requirements || !item.requirements[reqIndex]) return;
    var need = item.requirements[reqIndex].need;
    item.props = (item.props || []).slice();
    var prop = null;
    for (var i = 0; i < item.props.length; i++) if (item.props[i].name === need.field) { prop = item.props[i]; break; }
    if (prop) {
      if (lineValues(item, need.field).indexOf(need.value) === -1) prop.value = prop.value ? prop.value + '; ' + need.value : need.value;
    } else {
      item.props.push({ name: need.field, value: need.value });
    }
    item.propsKey = propsKey(item.props);
    item.notice = '';

    // Merge with an identical line if one already exists
    for (var j = 0; j < items.length; j++) {
      if (j !== index && String(items[j].variantId) === String(item.variantId) && (items[j].propsKey || '') === item.propsKey) {
        items[j].qty = (parseInt(items[j].qty, 10) || 0) + (parseInt(item.qty, 10) || 0);
        items.splice(index, 1);
        index = j > index ? j - 1 : j;
        break;
      }
    }
    var confirmText = 'Added: ' + need.value;
    confirmations[lineKey(items[index])] = { text: confirmText, until: Date.now() + CONFIRM_MS };
    saveItems(items);
    announce(confirmText + ' (' + items[index].title + ')');
    setTimeout(renderAll, CONFIRM_MS + 50);
    // The button is gone after re-render; keep keyboard focus on the updated line.
    var line = listEl && listEl.querySelectorAll('.quote-line')[index];
    var focusEl = line && line.querySelector('.quote-line__title');
    if (focusEl) focusEl.focus();
  }

  function propsKey(props) {
    return (props || []).map(function (p) { return p.name + '=' + p.value; }).join('|');
  }

  function addToQuote(button) {
    var product = parseJSON(button.getAttribute('data-quote-product'));
    if (!product || !product.variants || !product.variants.length) return;

    // Figure out which variant is selected: the product form's id field wins,
    // then the button's default variant, then the first variant.
    var scope = button.closest('form') || button.closest('product-form') || button.closest('.product-form');
    var variantId = null;
    var qty = 1;

    if (scope) {
      var idField = scope.querySelector('[name="id"]');
      if (idField && idField.value) variantId = idField.value;

      if (button.getAttribute('data-quote-context') === 'page') {
        var qtyField = scope.querySelector('input[name="quantity"]');
        if (qtyField) qty = Math.max(1, parseInt(qtyField.value, 10) || 1);
      }
    }

    var variant = findVariant(product, variantId) ||
      findVariant(product, button.getAttribute('data-quote-variant')) ||
      product.variants[0];

    var props = collectProps(scope, button, product);
    var rules = collectRules(scope, button, product);
    var requirements = collectRequirements(scope, button, product);
    var key = propsKey(props);

    var items = loadItems();
    var existing = null;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variantId) === String(variant.id) && (items[i].propsKey || '') === key) { existing = items[i]; break; }
    }

    if (existing) {
      existing.qty = (parseInt(existing.qty, 10) || 0) + qty;
    } else {
      items.push({
        productId: product.id,
        title: product.title,
        vendor: product.vendor || '',
        url: product.url,
        image: product.image || '',
        variantId: variant.id,
        variantTitle: variant.title,
        sku: variant.sku || '',
        qty: qty,
        props: props,
        propsKey: key,
        rules: rules,
        requirements: requirements,
        variants: product.variants.map(function (v) { return { id: v.id, title: v.title, sku: v.sku || '' }; })
      });
    }

    saveItems(items);
    flashButton(button);
    openDrawer();
  }

  function flashButton(button) {
    var label = button.querySelector('.quote-btn__label') || button;
    var original = button.getAttribute('data-quote-label') || label.textContent;
    button.setAttribute('data-quote-label', original);
    label.textContent = 'Added to quote ✓';
    button.classList.add('is-added');
    clearTimeout(button.__quoteTimer);
    button.__quoteTimer = setTimeout(function () {
      label.textContent = original;
      button.classList.remove('is-added');
    }, 2000);
  }

  // "Request a quote for this cart": copy cart lines into the quote list
  // (read-only; the Shopify cart is left untouched).
  function addCartToQuote(button) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    button.disabled = true;
    fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var lines = (cart && cart.items) || [];
        var items = loadItems();
        lines.forEach(function (li) {
          var existing = null;
          for (var i = 0; i < items.length; i++) {
            if (String(items[i].variantId) === String(li.variant_id)) { existing = items[i]; break; }
          }
          if (existing) {
            existing.qty = Math.max(parseInt(existing.qty, 10) || 0, parseInt(li.quantity, 10) || 1);
            return;
          }
          var vTitle = li.variant_title || 'Default Title';
          var img = li.image || '';
          if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=160';
          items.push({
            productId: li.product_id,
            title: li.product_title || li.title,
            vendor: ({ 'Hillrom': 'Welch Allyn', 'Conmed Corporation': 'ConMed' })[li.vendor] || li.vendor || '',
            url: '/products/' + li.handle,
            image: img,
            variantId: li.variant_id,
            variantTitle: vTitle,
            sku: li.sku || '',
            qty: parseInt(li.quantity, 10) || 1,
            variants: [{ id: li.variant_id, title: vTitle, sku: li.sku || '' }]
          });
        });
        saveItems(items);
        if (lines.length) flashButton(button);
        openDrawer();
      })
      .catch(function () { openDrawer(); })
      .then(function () { button.disabled = false; });
  }

  // Place the "Request a quote for this cart" block under the cart page checkout
  // button and the mini-cart buttons. Re-applied when the theme re-renders the cart.
  function placeCartQuoteButtons() {
    var tpl = document.getElementById('quote-cart-cta-template');
    if (!tpl) return;
    document.querySelectorAll('.cart-recap__checkout').forEach(function (checkout) {
      var parent = checkout.parentNode;
      if (!parent || parent.querySelector('.quote-cart-cta')) return;
      checkout.insertAdjacentElement('afterend', tpl.content.firstElementChild.cloneNode(true));
      var routesTpl = document.getElementById('quote-buy-routes-template');
      if (routesTpl && routesTpl.content.firstElementChild && !parent.querySelector('.buy-routes')) {
        parent.querySelector('.quote-cart-cta').insertAdjacentElement('afterend', routesTpl.content.firstElementChild.cloneNode(true));
      }
    });
    document.querySelectorAll('.mini-cart__button-container').forEach(function (box) {
      if (box.querySelector('.quote-cart-cta')) return;
      box.appendChild(tpl.content.firstElementChild.cloneNode(true));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Drawer                                                               */
  /* ------------------------------------------------------------------ */

  var drawer, listEl, emptyEl, formWrap, successEl, errorEl, form, submitBtn, lastFocus, jumpWrap, errorKind = '';

  function cacheDrawer() {
    drawer = document.getElementById('quote-drawer');
    if (!drawer) return false;
    listEl = drawer.querySelector('[data-quote-list]');
    emptyEl = drawer.querySelector('[data-quote-empty]');
    formWrap = drawer.querySelector('[data-quote-form-wrap]');
    successEl = drawer.querySelector('[data-quote-success]');
    errorEl = drawer.querySelector('[data-quote-error]');
    form = drawer.querySelector('[data-quote-form]');
    submitBtn = drawer.querySelector('[data-quote-submit]');
    jumpWrap = drawer.querySelector('[data-quote-jump-wrap]');
    var panel = drawer.querySelector('.quote-drawer__panel') || drawer;
    if (!drawer.querySelector('[data-quote-live]')) {
      var live = document.createElement('p');
      live.className = 'visually-hidden';
      live.setAttribute('data-quote-live', '');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      panel.appendChild(live);
    }
    // Small screens: hide the sticky "Continue to details" bar while the Submit button is on screen.
    var body = drawer.querySelector('.quote-drawer__body');
    if (jumpWrap && submitBtn && body && window.IntersectionObserver && !drawer.__jumpObserver) {
      drawer.__jumpObserver = new IntersectionObserver(function (entries) {
        drawer.classList.toggle('is-at-submit', entries[0].isIntersecting);
      }, { root: body, threshold: 0.5 });
      drawer.__jumpObserver.observe(submitBtn);
    }
    return true;
  }

  // "Continue to details": scroll the drawer to the details form and focus the first field.
  function jumpToDetails() {
    if (!drawer && !cacheDrawer()) return;
    var heading = formWrap && formWrap.querySelector('.quote-form__heading');
    var target = heading || form;
    if (target && target.scrollIntoView) target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    var first = form && form.querySelector('[name="quote_name"]');
    if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }, 350);
  }

  // Modal dialog behaviour (W3C APG): focus moves into the drawer on open, the page
  // behind it is inert, Tab/Shift+Tab cycle inside the panel, Escape closes and
  // returns focus to the trigger.
  var inertNodes = [];
  function setBackgroundInert(on) {
    if (on) {
      inertNodes = [];
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === drawer || el.contains(drawer) || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
        if (el.hasAttribute('inert')) return;
        el.setAttribute('inert', '');
        el.setAttribute('data-quote-inert', '');
        inertNodes.push(el);
      });
    } else {
      inertNodes.forEach(function (el) { el.removeAttribute('inert'); el.removeAttribute('data-quote-inert'); });
      inertNodes = [];
    }
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'), function (el) {
      return el.offsetParent !== null && !el.closest('[hidden]') && !el.closest('.quote-hp');
    });
  }

  function openDrawer() {
    if (!drawer && !cacheDrawer()) return;
    lastFocus = document.activeElement;
    successEl.hidden = true;
    renderAll();
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('quote-drawer-open');
    document.querySelectorAll('[data-quote-open]').forEach(function (el) { el.setAttribute('aria-expanded', 'true'); });
    setBackgroundInert(true);
    var heading = drawer.querySelector('#quote-drawer-title');
    if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    var target = heading || drawer.querySelector('[data-quote-close]');
    // Focus after the panel is visible; retry once in case the opening transition delays it.
    setTimeout(function () { if (target) target.focus(); }, 50);
    setTimeout(function () { if (target && !drawer.contains(document.activeElement)) target.focus(); }, 350);
  }

  function closeDrawer() {
    if (!drawer) return;
    // Line notices ("Removed ...") have been seen once; don't keep showing them.
    var items = loadItems();
    var hadNotice = items.some(function (it) { return it && it.notice; });
    if (hadNotice) {
      items.forEach(function (it) { if (it) it.notice = ''; });
      saveItems(items);
    }
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('quote-drawer-open');
    document.querySelectorAll('[data-quote-open]').forEach(function (el) { el.setAttribute('aria-expanded', 'false'); });
    setBackgroundInert(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // Focus trap (also covers browsers without `inert`).
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab' || !drawer || !drawer.classList.contains('is-open')) return;
    var panel = drawer.querySelector('.quote-drawer__panel') || drawer;
    var list = focusables(panel);
    if (!list.length) { event.preventDefault(); return; }
    var first = list[0], last = list[list.length - 1];
    var active = document.activeElement;
    var inside = panel.contains(active);
    if (event.shiftKey) {
      if (!inside || active === first || active === panel.querySelector('#quote-drawer-title')) { event.preventDefault(); last.focus(); }
    } else {
      if (!inside || active === last) { event.preventDefault(); first.focus(); }
    }
  });

  function renderCounts(items) {
    var count = totalQty(items);
    document.querySelectorAll('[data-quote-count]').forEach(function (el) { el.textContent = count; });
    document.querySelectorAll('[data-quote-badge]').forEach(function (el) {
      el.classList.toggle('is-empty', count === 0);
    });
    // One clear accessible name instead of "2 View Quote List (2) View Quote List".
    document.querySelectorAll('.quote-header-link').forEach(function (el) {
      el.setAttribute('aria-label', 'View quote list, ' + count + (count === 1 ? ' item' : ' items'));
    });
  }

  function renderList(items) {
    if (!drawer && !cacheDrawer()) return;
    var showSuccess = !successEl.hidden;

    emptyEl.hidden = items.length > 0 || showSuccess;
    formWrap.hidden = items.length === 0 || showSuccess;
    listEl.hidden = showSuccess;
    if (jumpWrap) jumpWrap.hidden = items.length === 0 || showSuccess;
    var now = Date.now();

    // Re-rendering replaces the lines; remember which control had focus so keyboard users keep their place.
    var refocus = null;
    var active = document.activeElement;
    if (active && active !== listEl && listEl.contains(active)) {
      var activeLine = active.closest('.quote-line');
      var activeCls = (active.className && typeof active.className === 'string') ? active.className.split(' ')[0] : '';
      if (activeLine && activeCls) refocus = { line: Array.prototype.indexOf.call(listEl.children, activeLine), sel: '.' + activeCls };
    }

    listEl.innerHTML = items.map(function (item, index) {
      var options = '';
      if (item.variants && item.variants.length > 1) {
        options = '<label class="quote-line__option">' +
          '<span class="visually-hidden">Option</span>' +
          '<select data-quote-variant-select data-index="' + index + '">' +
          item.variants.map(function (v) {
            return '<option value="' + esc(v.id) + '"' + (String(v.id) === String(item.variantId) ? ' selected' : '') + '>' + esc(v.title) + '</option>';
          }).join('') +
          '</select></label>';
      } else if (!isDefaultTitle(item.variantTitle)) {
        options = '<p class="quote-line__meta">' + esc(item.variantTitle) + '</p>';
      }

      return '<li class="quote-line">' +
        '<a class="quote-line__image" href="' + esc(item.url) + '" aria-label="' + esc(item.title) + '">' +
          (item.image ? '<img src="' + esc(item.image) + '" alt="" loading="lazy" width="72" height="72">' : '') +
        '</a>' +
        '<div class="quote-line__info">' +
          (item.vendor ? '<p class="quote-line__vendor">' + esc(item.vendor) + '</p>' : '') +
          '<a class="quote-line__title" href="' + esc(item.url) + '">' + esc(item.title) + '</a>' +
          options +
          (item.sku ? '<p class="quote-line__meta">SKU: ' + esc(item.sku) + '</p>' : '') +
          (item.props && item.props.length ? item.props.map(function (p) {
            return '<p class="quote-line__meta">' + esc(p.name) + ': ' + esc(p.value) + '</p>';
          }).join('') : '') +
          (item.notice ? '<div class="quote-line__notice quote-line__notice--dismissible" role="alert"><p class="quote-line__notice-text">' + esc(item.notice) + '</p>' +
            '<button type="button" class="quote-line__notice-dismiss" data-quote-notice-dismiss data-index="' + index + '" aria-label="Dismiss this message">&times;</button></div>' : '') +
          unmetRequirements(item).map(function (req) {
            var k = item.requirements.indexOf(req);
            return '<div class="quote-line__notice quote-line__notice--req" data-quote-req-notice tabindex="-1">' +
              '<p class="quote-line__req-text">' + esc(req.message || ('This configuration also needs ' + req.need.value + '.')) +
              (req.link ? ' <a class="quote-line__req-link" href="' + esc(req.link) + '">View ' + esc(partNumber(req.need.value)) + '</a>' : '') + '</p>' +
              '<button type="button" class="quote-line__req-add" data-quote-req-add data-index="' + index + '" data-req="' + k + '" aria-label="' + esc(requirementLabel(item, req) + ' to the ' + item.title + ' line') + '">' + esc(requirementLabel(item, req)) + '</button>' +
            '</div>';
          }).join('') +
          (function () {
            var c = confirmations[lineKey(item)];
            if (!c) return '';
            if (c.until <= now) { delete confirmations[lineKey(item)]; return ''; }
            return '<p class="quote-line__confirm">&#10003; ' + esc(c.text) + '</p>';
          })() +
          '<div class="quote-line__actions">' +
            '<div class="quote-qty">' +
              '<button type="button" class="quote-qty__btn" data-quote-dec data-index="' + index + '" aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" class="quote-qty__input" min="1" step="1" inputmode="numeric" value="' + esc(item.qty) + '" data-quote-qty data-index="' + index + '" aria-label="Quantity">' +
              '<button type="button" class="quote-qty__btn" data-quote-inc data-index="' + index + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="quote-line__remove" data-quote-remove data-index="' + index + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</li>';
    }).join('');

    if (refocus && refocus.line > -1) {
      var line = listEl.children[refocus.line];
      var target = line && line.querySelector(refocus.sel);
      if (target && target.focus) target.focus();
    }
  }

  function renderAll() {
    var items = loadItems();
    renderCounts(items);
    renderList(items);
    clearResolvedError(items);
  }

  // Drop a drawer error once its cause is gone: required hardware added, the
  // "options removed" notices dismissed/cleared, or items added to an empty list.
  function clearResolvedError(items) {
    if (!errorEl || errorEl.hidden || !errorKind) return;
    var resolved = false;
    if (errorKind === 'req') resolved = !items.some(function (it) { return unmetRequirements(it).length > 0; });
    else if (errorKind === 'rules') resolved = !items.some(function (it) { return it && it.notice; });
    else if (errorKind === 'empty') resolved = items.length > 0;
    if (resolved) setError('');
  }

  function updateItem(index, fn) {
    var items = loadItems();
    if (!items[index]) return;
    items[index].notice = '';
    fn(items[index], items);
    saveItems(items.filter(function (i) { return i && (parseInt(i.qty, 10) || 0) > 0; }));
  }

  function changeVariant(index, newId) {
    var items = loadItems();
    var item = items[index];
    if (!item) return;
    var v = findVariant(item, newId);
    if (!v) return;

    // Re-apply the product's compatibility rules to the new base (e.g. a heated top
    // needs a heated-top ready base); incompatible options are removed and explained.
    var trial = { variantTitle: v.title, props: (item.props || []).slice(), rules: item.rules || [] };
    var removed = enforceRules(trial);
    item.props = trial.props;
    item.propsKey = propsKey(item.props);
    item.notice = removed.length ? removed.join(' ') : '';

    // Merge with an existing line for the same variant
    for (var i = 0; i < items.length; i++) {
      if (i !== index && String(items[i].variantId) === String(v.id) && (items[i].propsKey || '') === (item.propsKey || '')) {
        items[i].qty = (parseInt(items[i].qty, 10) || 0) + (parseInt(item.qty, 10) || 0);
        items.splice(index, 1);
        saveItems(items);
        return;
      }
    }

    item.variantId = v.id;
    item.variantTitle = v.title;
    item.sku = v.sku || '';
    saveItems(items);
  }

  /* ------------------------------------------------------------------ */
  /* Submission                                                           */
  /* ------------------------------------------------------------------ */

  function buildItemsText(items) {
    var base = (CFG.shopUrl || window.location.origin).replace(/\/$/, '');
    return items.map(function (item, i) {
      var lines = [(i + 1) + '. Qty ' + item.qty + ' x ' + item.title];
      if (!isDefaultTitle(item.variantTitle)) lines.push('   Option: ' + item.variantTitle);
      if (item.sku) lines.push('   SKU: ' + item.sku);
      (item.props || []).forEach(function (p) { lines.push('   ' + p.name + ': ' + p.value); });
      lines.push('   ' + base + item.url + (item.url.indexOf('?') > -1 ? '&' : '?') + 'variant=' + item.variantId);
      return lines.join('\n');
    }).join('\n\n');
  }

  function setError(msg, kind) {
    if (!errorEl) return;
    errorEl.textContent = msg || '';
    errorEl.hidden = !msg;
    errorKind = msg ? (kind || 'other') : '';
  }

  function submitQuote(event) {
    event.preventDefault();
    setError('');

    var items = loadItems();
    if (!items.length) { setError('Your quote list is empty.', 'empty'); return; }

    // Verify compatibility again before sending; never submit an unbuildable line.
    var fixed = false;
    items.forEach(function (it) { var r = enforceRules(it); if (r.length) { it.notice = r.join(' '); fixed = true; } });
    if (fixed) {
      saveItems(items);
      setError('One or more options were not compatible with the selected model and have been removed. Please review the highlighted lines, then submit again.', 'rules');
      return;
    }

    // Required companion hardware: never submit a line that is missing it.
    var missing = items.filter(function (it) { return unmetRequirements(it).length > 0; });
    if (missing.length) {
      renderAll();
      setError('One or more lines need required hardware (highlighted). Add it, or change the configuration, then submit again.', 'req');
      var firstNotice = listEl && listEl.querySelector('[data-quote-req-notice]');
      if (firstNotice) {
        if (firstNotice.scrollIntoView) firstNotice.scrollIntoView({ block: 'center' });
        firstNotice.focus();
      }
      return;
    }

    if (form.querySelector('[name="quote_website"]').value) return; // honeypot

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (!CFG.portalId || !CFG.formGuid || CFG.formGuid.indexOf('__') === 0) {
      setError('Quote requests are temporarily unavailable. Please call or email us.');
      return;
    }

    var fullName = form.querySelector('[name="quote_name"]').value.trim();
    var nameParts = fullName.split(/\s+/);
    var firstName = nameParts.shift() || '';
    var lastName = nameParts.join(' ');

    function fieldVal(name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? String(el.value || '').trim() : '';
    }
    var details = [];
    if (fieldVal('quote_zip')) details.push('Delivery ZIP: ' + fieldVal('quote_zip'));
    if (fieldVal('quote_gpo')) details.push('GPO affiliation: ' + fieldVal('quote_gpo'));
    if (fieldVal('quote_needed_by')) details.push('Needed by: ' + fieldVal('quote_needed_by'));
    var messageText = [details.join('\n'), fieldVal('quote_message')].filter(function (p) { return p !== ''; }).join('\n\n');

    var fields = [
      { objectTypeId: '0-1', name: 'firstname', value: firstName },
      { objectTypeId: '0-1', name: 'lastname', value: lastName },
      { objectTypeId: '0-1', name: 'email', value: form.querySelector('[name="quote_email"]').value.trim() },
      { objectTypeId: '0-1', name: 'phone', value: form.querySelector('[name="quote_phone"]').value.trim() },
      { objectTypeId: '0-1', name: 'company', value: form.querySelector('[name="quote_company"]').value.trim() },
      { objectTypeId: '0-1', name: 'message', value: messageText },
      { objectTypeId: '0-1', name: 'quote_request_items', value: buildItemsText(items) }
    ].filter(function (f) { return f.value !== ''; });

    var context = { pageUri: window.location.href, pageName: document.title };
    var hutk = getCookie('hubspotutk');
    if (hutk) context.hutk = hutk;

    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');

    fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + CFG.portalId + '/' + CFG.formGuid, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ submittedAt: String(Date.now()), fields: fields, context: context })
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok) {
            var msg = (data && data.errors && data.errors[0] && data.errors[0].message) || 'Request failed';
            throw new Error(msg);
          }
          return data;
        });
      })
      .then(function () {
        saveItems([]);
        form.reset();
        successEl.hidden = false;
        renderAll();
        successEl.focus();
      })
      .catch(function (err) {
        var msg = String(err && err.message || '');
        if (/phone/i.test(msg)) {
          setError('Please check your phone number (include the area code) and try again.');
        } else if (/email/i.test(msg)) {
          setError('Please check your email address and try again.');
        } else {
          setError('Sorry, we couldn’t send your request. Please try again, or contact us directly.');
        }
      })
      .then(function () {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
      });
  }

  /* ------------------------------------------------------------------ */
  /* $0 variants: hide Add to Cart + price, never allow them in the cart  */
  /* ------------------------------------------------------------------ */

  function formIsQuoteOnly(el) {
    var f = el && el.closest ? el.closest('form') : null;
    if (!f) return false;
    var flag = f.querySelector('[data-quote-zero]');
    return !!(flag && flag.getAttribute('data-quote-zero') === 'true');
  }

  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    var origin = event.target && event.target.nodeType === 1 ? event.target : null;
    var scopes = [];

    if (origin) {
      var f = origin.closest('form') || (origin.querySelector && origin.querySelector('form.product-form'));
      if (f) scopes.push(f);
    }
    if (!scopes.length) scopes = Array.prototype.slice.call(document.querySelectorAll('form.product-form'));

    scopes.forEach(function (f) {
      var isZero = !!variant && Number(variant.price) === 0;
      f.querySelectorAll('[data-quote-zero]').forEach(function (flag) {
        if (flag.hasAttribute('data-quote-always')) isZero = true;
        flag.setAttribute('data-quote-zero', isZero ? 'true' : 'false');
      });
      f.classList.toggle('is-quote-only', isZero);
    });
  });

  // Safety net: block any add-to-cart for a $0 / quote-only product form.
  document.addEventListener('submit', function (event) {
    var f = event.target;
    if (!f || !f.getAttribute) return;
    var action = f.getAttribute('action') || '';
    if (action.indexOf('/cart/add') > -1 && formIsQuoteOnly(f)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('.product-form__add-button, .shopify-payment-button__button');
    if (btn && formIsQuoteOnly(btn)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  /* ------------------------------------------------------------------ */
  /* Events                                                               */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t.closest) return;

    var cartBtn = t.closest('[data-quote-from-cart]');
    if (cartBtn) {
      event.preventDefault();
      event.stopPropagation();
      addCartToQuote(cartBtn);
      return;
    }

    var addBtn = t.closest('[data-quote-add]');
    if (addBtn) {
      event.preventDefault();
      event.stopPropagation();
      addToQuote(addBtn);
      return;
    }

    if (t.closest('[data-quote-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (t.closest('[data-quote-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var dismiss = t.closest('[data-quote-notice-dismiss]');
    if (dismiss) {
      event.preventDefault();
      var di = +dismiss.getAttribute('data-index');
      var dItems = loadItems();
      if (dItems[di]) {
        dItems[di].notice = '';
        saveItems(dItems);
        var dLine = listEl && listEl.querySelectorAll('.quote-line')[di];
        var dFocus = dLine && dLine.querySelector('.quote-line__title');
        if (dFocus) dFocus.focus();
      }
      return;
    }

    if (t.closest('[data-quote-jump]')) {
      event.preventDefault();
      jumpToDetails();
      return;
    }

    var reqAdd = t.closest('[data-quote-req-add]');
    if (reqAdd) {
      event.preventDefault();
      addRequirement(+reqAdd.getAttribute('data-index'), +reqAdd.getAttribute('data-req'));
      return;
    }

    var inc = t.closest('[data-quote-inc]');
    var dec = t.closest('[data-quote-dec]');
    var rem = t.closest('[data-quote-remove]');

    if (inc) updateItem(+inc.getAttribute('data-index'), function (i) { i.qty = (parseInt(i.qty, 10) || 0) + 1; });
    if (dec) updateItem(+dec.getAttribute('data-index'), function (i) { i.qty = Math.max(1, (parseInt(i.qty, 10) || 1) - 1); });
    if (rem) updateItem(+rem.getAttribute('data-index'), function (i) { i.qty = 0; });

    if (t.closest('[data-quote-continue]')) {
      event.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('change', function (event) {
    var t = event.target;
    if (t.matches && t.matches('[data-quote-qty]')) {
      var val = Math.max(1, parseInt(t.value, 10) || 1);
      updateItem(+t.getAttribute('data-index'), function (i) { i.qty = val; });
    }
    if (t.matches && t.matches('[data-quote-variant-select]')) {
      changeVariant(+t.getAttribute('data-index'), t.value);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && drawer && drawer.classList.contains('is-open')) closeDrawer();
  });

  // Keep multiple tabs in sync
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) renderAll();
  });

  function init() {
    // Move the header badge next to the cart icon
    var tpl = document.getElementById('quote-header-item-template');
    var cartItem = document.querySelector('.header__action-item--cart');
    if (tpl && cartItem && !document.querySelector('.header__action-item--quote')) {
      cartItem.parentNode.insertBefore(tpl.content.cloneNode(true), cartItem);
    }

    if (cacheDrawer()) {
      form.addEventListener('submit', submitQuote);
      var minDate = form.querySelector('[data-quote-min-today]');
      if (minDate) {
        var now = new Date();
        minDate.min = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      }
    }
    renderAll();

    placeCartQuoteButtons();
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; placeCartQuoteButtons(); }, 40);
      }).observe(document.body, { childList: true, subtree: true });
    }

    if (window.location.hash === '#quote') openDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.QuoteRequest = { open: openDrawer, close: closeDrawer, items: loadItems };
})();
